import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  InputAdornment,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Button,
} from "@mui/material";
import PropTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { monthEndStyles } from "components/Styles/monthEndStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { StyledAgGridContainer } from "components/StyledComponents/styledComponents.js";
import URLs from "constants/urls";
import { StyledCancelButton } from "components/StyledComponents/styledComponents";
import { StyledSnackbar, StyledAlert } from "components/StyledComponents/styledComponents";
const UserCallLogs = () => {
  const [callLogs, setCallLogs] = useState([]);
  const [allCallLogs, setAllCallLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailGridData, setDetailGridData] = useState([]);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const todayDate = new Date().toLocaleDateString("en-GB").split("/").reverse().join("-");

  const fetchCallLogs = async (searchText = "") => {
    setLoading(true);
    try {
      const res = await fetch(URLs.getAllCallLogs, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          CurrentDate: todayDate,
          username: searchText,
        }),
      });
      const result = await res.json();
      const formatted = result.map((item) => ({
        executiveName: item.username.trim(),
        loginTime: new Date(item.LoginDate).toLocaleString(),
        loginLocation: item.LoginLocation,
        logoutTime: new Date(item.LogoutDate).toLocaleString(),
        logoutLocation: item.LogoutLocation,
        callLogs: item.CalllogCount,
      }));
      setCallLogs(formatted);
      setAllCallLogs(formatted);
    } catch (error) {
      setAlert("Error fetching daily call logs:", error);
    }
    setLoading(false);
  };
  // ✅ FETCH API #3 ON LOAD
  useEffect(() => {
    fetchCallLogs();
  }, []);

  // ✅ FILTER HANDLER
  const handleFilter = (e) => {
    const val = e.target.value.toLowerCase();
    setSearchTerm(val);
    const filtered = allCallLogs.filter(
      (item) =>
        item.executiveName.toLowerCase().includes(val) ||
        item.loginLocation.toLowerCase().includes(val)
    );
    setCallLogs(filtered);
  };
  const handleClose = () => setOpen(false);

  const handleDateFetch = async (username, selectedDate, isView) => {
    setLoading(true);
    try {
      const res = await fetch(URLs.getCallLogsById, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          CurrentDate: selectedDate,
          username: username.trim(),
        }),
      });

      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }

      const result = await res.json();

      const formatted = result.map((item) => ({
        callTimestamp: new Date(item.CallTimestamp).toLocaleString(),
        callType: item.CallType,
        duration: `${item.Duration} sec`,
        phoneNumber: item.Phonenumber,
        username: item.username,
      }));

      setDetailGridData(formatted);
      isView ? setOpenViewDialog(true) : setOpenDetailDialog(true);
    } catch (err) {
      console.error("Error fetching call details:", err);
    }
    setLoading(false);
  };

  // ✅ ACTION CELL
  const ActionCellRenderer = (props) => {
    const { data } = props;

    const handleChange = (e) => {
      const date = e.target.value;
      setSelectedDate(date);
    };

    const handleViewClick = () => {
      setOpenViewDialog(true);
      handleDateFetch(data.executiveName, selectedDate, true); // pass isView = true
    };

    return (
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <input
          type="date"
          value={selectedDate}
          onChange={handleChange}
          style={{ padding: "4px" }}
        />
        <IconButton onClick={handleViewClick}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  ActionCellRenderer.propTypes = {
    data: PropTypes.object.isRequired,
    context: PropTypes.shape({
      handleDateFetch: PropTypes.func.isRequired,
    }).isRequired,
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const callLogsColumnDefs = [
    { headerName: "Executive Name", field: "executiveName", flex: 1 },
    { headerName: "Login Time", field: "loginTime", flex: 1 },
    { headerName: "Login Location", field: "loginLocation", flex: 1 },
    { headerName: "Logout Time", field: "logoutTime", flex: 1 },
    { headerName: "Logout Location", field: "logoutLocation", flex: 1 },
    { headerName: "Call Logs", field: "callLogs", flex: 1 },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      cellRenderer: ActionCellRenderer,
    },
  ];

  const detailColumnDefs = [
    { headerName: "Call Timestamp", field: "callTimestamp", flex: 1 },
    { headerName: "Call Type", field: "callType", flex: 1 },
    { headerName: "Duration", field: "duration", flex: 1 },
    { headerName: "Phone Number", field: "phoneNumber", flex: 1 },
    { headerName: "Username", field: "username", flex: 1 },
  ];

  return (
    <DashboardLayout>
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box
          display="flex"
          sx={{ pt: 1, pb: 2 }}
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            placeholder="Search by executive or location..."
            onChange={handleFilter}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <StyledAgGridContainer component={Paper} className={`ag-theme-alpine`}>
          <AgGridReact
            enableCellTextSelection={true}
            enableRangeSelection={true}
            ref={gridRef}
            rowData={callLogs}
            columnDefs={callLogsColumnDefs}
            pagination={true}
            defaultColDef={{
              filter: true, // Enable filtering by default
              sortable: true, // Optional: allow sorting too
              resizable: true, // Optional: allow resizing columns
              floatingFilter: true,
            }}
            paginationPageSize={10}
            context={{ handleDateFetch }}
          />
        </StyledAgGridContainer>

        {/* View Popup */}
        <Dialog
          open={openViewDialog}
          fullWidth
          maxWidth="lg"
          onClose={() => setOpenViewDialog(false)}
        >
          <DialogTitle>Call Log Details</DialogTitle>
          <DialogContent>
            <Box className="ag-theme-alpine" style={{ height: 300, width: "100%" }}>
              <AgGridReact
                enableCellTextSelection={true}
                enableRangeSelection={true}
                rowData={detailGridData}
                columnDefs={detailColumnDefs}
                pagination={true}
                defaultColDef={{
                  filter: true, // Enable filtering by default
                  sortable: true, // Optional: allow sorting too
                  resizable: true, // Optional: allow resizing columns
                  floatingFilter: true,
                }}
                paginationPageSize={10}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <StyledCancelButton onClick={() => setOpenViewDialog(false)} color="error">
              Cancel
            </StyledCancelButton>
          </DialogActions>
        </Dialog>
      </TableContainer>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            padding: "clamp(10px, 2vw, 20px)",
            minWidth: "clamp(200px, 30vw, 300px)",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "clamp(10px, 2vw, 20px)",
          }}
        >
          <CircularProgress color="primary" size="clamp(24px, 4vw, 40px)" />
          <Typography variant="h6" sx={{ fontSize: "clamp(14px, 2vw, 20px)" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
        sx={{
          width: "clamp(250px, 50vw, 400px)",
          top: "clamp(10px, 2vh, 20px)",
        }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
    </DashboardLayout>
  );
};

export default UserCallLogs;
